import { mixins } from 'vue-class-component';
import { Component, Prop } from 'vue-property-decorator';
import FieldWrapper from '@shared/components/forms/inputs/field-wrapper/field-wrapper.vue';
import AbstractField from '@shared/components/forms/inputs/abstract-field/abstract-field';
import 'vue-select/dist/vue-select.css';
import EntityTableProvider from '@shared/components/tables/entity-table-provider/entity-table-provider.vue';
import AbstractTable from '@shared/components/tables/abstract-table/abstract-table';
import EntityTable from '@shared/components/tables/entity-table/entity-table.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BpcDrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import EntityDeleteModalProvider from '@shared/components/modals/entity-delete-modal-provider/entity-delete-modal-provider.vue';
import AccessControlBadgeForm from '@access-control/components/access-control-badge-form.vue';
import moment from 'moment';
import ReplaceAccessControlBadgeModal from '../../modals/replace-access-control-badge-modal/replace-access-control-badge-modal.vue';
import BlockAccessControlBadgeModal from '../../modals/block-access-control-badge-modal/block-access-control-badge-modal.vue';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { AccessControlBadge } from '@access-control/interfaces/access-control-badge';

@Component({
  name: 'bpc-access-control-badge-table-input',
  components: {
    'bpc-access-control-badge-form': AccessControlBadgeForm,
    'bpc-entity-table-provider': EntityTableProvider,
    'bpc-entity-table': EntityTable,
    'bpc-field-wrapper': FieldWrapper,
    'font-awesome-icon': FontAwesomeIcon,
    'bpc-drawer-modal': BpcDrawerModal,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-entity-delete-modal-provider': EntityDeleteModalProvider,
    'bpc-replace-access-control-badge-modal': ReplaceAccessControlBadgeModal,
    'bpc-block-access-control-badge-modal': BlockAccessControlBadgeModal,
  },
})
export default class AccessControlBadgeTableInput extends mixins(AbstractField, AbstractTable) {
  declare $refs: AbstractTable['$refs'] &
    AbstractField['$refs'] & {
      accessControlBadgeForm: InstanceType<typeof AccessControlBadgeForm> & {
        saveEntity(): Promise<BPCGenericApiResponse>;
        saving: boolean;
      };
    };
  @Prop(Array) readonly value: any[];
  @Prop(String) readonly selectEntity: string;
  @Prop(String) readonly selectPluralEntity: string;
  @Prop({ type: String, default: 'drawer' }) readonly type: string;
  @Prop(Array) readonly filters: any[];
  @Prop(String) readonly fieldForStorage: string;
  @Prop(Boolean) readonly withDraft: boolean;
  //@Prop(String) readonly formConfigName: string;
  @Prop({
    type: Array,
    default: () => ['configLoaded', 'paginationLoaded', 'orderLoaded'],
  })
  readonly customFlagsToCheckBeforeLoadEntities: Array<any>;
  public entity: any = null;
  public entityName = this.selectEntity;
  public pluralEntityName = this.selectPluralEntity;
  public isProcessing = false;
  public canceling = false;
  public permissionEntityNameToUse = 'access_control_badges';
  public showReplaceAccessControlBadgeModal = false;
  public showBlockAccessControlBadgeModal = false;
  public selectedAccessControlBadgeId: number = null;
  public dataTab: string = 'Data';
  timestamp = moment().unix();

  public customRowActions = {
    block: {
      activated: false,
      type: 'action',
      icon: 'minus-circle',
      show: false,
      event: 'block-access-control-badge',
      text: 'block',
      permissions: [],
    },
    unblock: {
      activated: false,
      type: 'action',
      icon: 'play-circle',
      show: false,
      event: 'unblock-access-control-badge',
      text: 'unblock',
      permissions: [],
    },
    replace: {
      activated: false,
      type: 'action',
      icon: 'sync',
      show: false,
      event: 'replace-access-control-badge',
      text: 'replace',
      permissions: [],
    },
    cancel: {
      activated: false,
      type: 'action',
      icon: 'ban',
      show: false,
      event: 'cancel',
      text: 'deactivate',
      permissions: ['cancel'],
    },
  };

  get formCancelPortalTarget(): string {
    return `access-control-badge-form-cancel-portal-target-${this.timestamp}`;
  }

  onEntitySaved() {
    //this.$emit('entity-saved');
    this.$refs.provider.getEntities();
    this.showDrawerSection = false;
  }

  get formActionsPortalTarget(): string {
    return `access-control-badge-form-actions-portal-target-${this.timestamp}`;
  }

  get showedFields() {
    const showedFields = [];
    for (const field of this.$refs.provider.fields) {
      if (!field['conditionalShow']) {
        showedFields.push(field);
      } else {
        const keys = field['conditionalShow']['fieldPath'].split('.');
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let conditionalValue = this;
        for (const key of keys) {
          if (conditionalValue[key] != 'undefined') {
            conditionalValue = conditionalValue[key];
          } else {
            break;
          }
        }
        if (conditionalValue === field['conditionalShow']['value']) {
          showedFields.push(field);
        }
      }
    }
    return showedFields;
  }

  get saving() {
    return this.$refs.accessControlBadgeForm?.saving || false;
  }

  get modalTitle() {
    if (this.formModel && this.formModel.id) {
      return (
        this.$t('global.modals.edit') + ' ' + this.$t('global.models.' + this.entityName + '.name')
      );
    } else {
      return (
        this.$t('global.modals.create') +
        ' ' +
        this.$t('global.models.' + this.entityName + '.name')
      );
    }
  }

  get tenantId(): number {
    return this.$store.getters['tenant/selectedTenantId'];
  }

  get cancelModalTitle() {
    return this.$t(`global.access_control_badge.cancel.title`, {
      entityName: this.$t('global.models.' + this.entityName + '.name'),
      entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
      thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
      thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
    });
  }

  get cancelModalBody() {
    return this.$t(`global.access_control_badge.cancel.body`, {
      entityName: this.$t('global.models.' + this.entityName + '.name'),
      entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
      thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
      thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
    });
  }

  updateRowsActions() {
    this.updatePageActions();
    this.updateRowActions();
    this.getSelection();
  }

  extendUpdateRowActions(entity: any) {
    entity.actions.edit.show = true;
    entity.actions.edit.activated = true;
    entity.actions.edit.permissions = ['update'];

    entity.actions.cancel.show = entity.status !== 'cancelled';
    entity.actions.cancel.activated = true;
    entity.actions.cancel.permissions = ['update'];

    entity.actions.replace.show = entity.status !== 'cancelled';
    entity.actions.replace.activated = true;
    entity.actions.replace.permissions = ['update'];

    if (entity.status === 'blocked') {
      entity.actions.unblock.show = true;
      entity.actions.unblock.activated = true;
      entity.actions.unblock.permissions = ['update'];
    } else if (entity.status === 'active') {
      entity.actions.block.show = true;
      entity.actions.block.activated = true;
      entity.actions.block.permissions = ['update'];
    }
  }

  onCloseReplaceAccessControlBadgeModal() {
    this.showReplaceAccessControlBadgeModal = false;
  }

  onReplacedAccessControlBadgeModal() {
    this.showReplaceAccessControlBadgeModal = false;
    this.$refs.provider.getEntities();
  }

  onCloseBlockAccessControlBadgeModal() {
    this.showBlockAccessControlBadgeModal = false;
  }

  onBlockAccessControlBadgeModal() {
    this.showBlockAccessControlBadgeModal = false;
    this.$refs.provider.getEntities();
  }

  unblockAccessControlBadge(item) {
    const path = `${this.$bpcApiRouteHelper.getEntityPath('access-control-badges')}/${
      item.item.id
    }/unblock-badge`;

    this.$bpcApi.post(path).subscribe();
    this.$refs.provider.getEntities();
  }

  customOnRowAction($event: { event: string; item: any }) {
    switch ($event.event) {
      case 'block-access-control-badge':
        this.showBlockAccessControlBadgeModal = true;
        this.selectedAccessControlBadgeId = $event.item.item.id;
        break;
      case 'unblock-access-control-badge':
        this.unblockAccessControlBadge($event.item);
        break;
      case 'replace-access-control-badge':
        this.showReplaceAccessControlBadgeModal = true;
        this.selectedAccessControlBadgeId = $event.item.item.id;
        break;
      case 'cancel':
        this.selectedAccessControlBadgeId = $event.item.item.id;
        this.onOpenCancelModal();
        break;
    }
  }

  onOpenCancelModal() {
    this.$bvModal.show('modal-cancel-access-control-badges');
  }

  onCloseCancelModal() {
    this.selectedAccessControlBadgeId = null;
    this.$bvModal.hide('modal-cancel-access-control-badges');
  }

  onConfirmCancelModal() {
    this.canceling = true;
    const basePath = this.$bpcApiRouteHelper.getEntityPath('access-control-badge');
    const url = basePath + `/${this.selectedAccessControlBadgeId}/cancel`;
    this.$bpcApi.put(url).subscribe(
      res => {
        this.$refs.provider.getEntities();
        this.canceling = false;
        this.onCloseCancelModal();
        this.$bpcMessage.showSuccessMessage({ text: 'global.generic.successful_action' });
      },
      error => {
        this.canceling = false;
        this.$bpcMessage.showErrorMessage({ text: 'global.access_control_badge.cancel.error' });
      },
    );
  }

  onFormSave(next: string = '') {
    // this.isSavingForm = true;
    console.log('Saving');
    this.nextAction = next;
    this.$refs.accessControlBadgeForm.saveEntity();
  }
}
